import { useEffect, useMemo } from 'react';
import { getSortField, getSortValue, sortKey } from '../../../../../features/table/helpers';
import {
  defaultViewStyle,
  pageKey,
  pageSizeKey,
  viewStyleKey,
  withArchiveKey,
} from '../../../../../helpers/path';
import { EmployeeType } from '../../../../../store/api/employees-api';
import { useBranchesStore } from '../../../../../store/use-branch-store';
import { useEmployeesStore } from '../../../../../store/use-employee-store';
import { useTagStaffStore } from '../../../../../store/use-tag-staff-store';
import { SortType } from '../../../../../types/props';
import { useEmployeesUtils } from './use-employees-utils';
import { formatDateTimeRangeFromString } from '../../../../../helpers/date-time';

export const useEmployees = () => {
  const { fetchEmployees, pagination, isLoadingFetch } = useEmployeesStore();
  const { fetchBranches } = useBranchesStore();
  const { fetchTags } = useTagStaffStore();
  const {
    t,
    restoreEmployee,
    params,
    createEmployeeModal,
    deleteEmployeeModal,
    jobTitlesModal,
    qrEmployeeModal,
    unlinkEmployeeModal,
  } = useEmployeesUtils();

  const place_ids = params['place_ids'];
  const ts = params['ts'];
  const employee = params['employee'];
  const tags = params['tags'];
  const createdAt = params['created_at'];
  const online = params['online'];
  const shift_state = params['shift_state'] as EmployeeType['shift_state'];
  const with_archive = !!params[withArchiveKey];
  const sort = params[sortKey];
  const sortField = getSortField(sort);
  const sortDirection = getSortValue(sort) as SortType;
  const viewStyle = params[viewStyleKey] || defaultViewStyle;
  const page = params[pageKey] || 1;
  const pageSize = params[pageSizeKey] || 10;

  const requestParams = useMemo(() => {
    const parsedCreatedAt = formatDateTimeRangeFromString(createdAt);

    return {
      is_by_company: true,
      page,
      page_size: pageSize,

      name: employee,
      tags,
      place_ids,
      from: parsedCreatedAt?.from,
      to: parsedCreatedAt?.to,
      online,
      shift_state,

      field: sortField,
      dir: sortDirection,
      with_archive,
    };
  }, [
    online,
    ts,
    employee,
    tags,
    createdAt,
    sortField,
    sortDirection,
    shift_state,
    place_ids,
    with_archive,
    page,
    pageSize,
    viewStyle,
  ]);

  useEffect(() => {
    fetchEmployees(requestParams);
  }, [requestParams]);

  useEffect(() => {
    fetchTags({ is_by_company: true, n: 1000, since_id: 0 });
    fetchBranches({ since_id: 0, n: 1000, with_archive: true });
  }, []);

  const handleEnd = () => {
    if (isLoadingFetch || !pagination || pagination.is_last) return;
    const nextPage = pagination.page + 1;
    fetchEmployees({ ...requestParams, page: nextPage, infinite: true });
  };

  return {
    t,
    createEmployeeModal,
    deleteEmployeeModal,
    unlinkEmployeeModal,
    jobTitlesModal,
    qrEmployeeModal,
    restoreEmployee,
    viewStyle,
    pagination,
    handleEnd,
  };
};
